/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {Fragment, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useCustomerId} from '@salesforce/commerce-sdk-react'
import useIntersectionObserver from '../../hooks/use-intersection-observer'
import {useCookieConsent} from '../../hooks/use-cookie-consent'
import Slider from '../slider'
import ProductTile from '../product-tile'
import {Text, Stack, useMultiStyleConfig} from '@chakra-ui/react'
import useTrackViewList from './hooks/use-track-view-list'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {trackSelectItem} from '../../tracking/analytics'
import {
    MAXIMUM_NUM_OF_RECOMMENDED_PRODUCTS,
    MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS
} from '../../constants'
import {isEqual, omit} from 'lodash'
import useEffectEvent from '../../hooks/use-effect-event'
import useRecommendations from '../../hooks/use-recommendations'
import useEinstein from '../../hooks/use-einstein'
import {useIntl} from 'react-intl'
import useProductItemsDetail from '../../hooks/useProductItemsDetail'
import useBreakpoints from '../../hooks/use-breakpoints'

/**
 * A component for fetching and rendering product recommendations from the Einstein API
 * by a zone or a recommender name.
 */
const RecommendedProducts = ({
    zone,
    recommender,
    products,
    title,
    shouldFetch,
    wrapper: Wrapper = Fragment,
    itemsPerSlide,
    trackingLabel,
    ...props
}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('RecommendedProducts', props)
    const {isAvailable, isCustom, isLoading, recommendations, getRecommendations} =
        useRecommendations(
            {recommender, zone},
            {maxRecommendations: MAXIMUM_NUM_OF_RECOMMENDED_PRODUCTS}
        )

    const {sendClickReco, sendViewReco} = useEinstein()
    const customerId = useCustomerId()
    const {marketing: marketingCookieAccepted} = useCookieConsent()
    const defaultItemsPerSlide = useBreakpoints({base: 2, lg: 4})
    itemsPerSlide = itemsPerSlide || defaultItemsPerSlide

    const [ref, isOnScreen] = useIntersectionObserver({useOnce: true})
    const {data: basket} = useCurrentBasket()
    const {data: productItemsDetail} = useProductItemsDetail(basket)

    /* If products < MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS hide the component */
    const isRecommendationsHidden =
        (recommendations?.recs?.length || 0) < MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS

    useTrackViewList({
        products: recommendations?.recs,
        trackingLabel,
        isRecommendationsHidden
    })

    // Fetch recommendations when something changes - useRecommendations takes care of deduplication
    useEffect(() => {
        // Return early if we have no Einstein API instance or not yet displayed
        if (!isAvailable || !customerId || !isOnScreen) {
            return
        }

        // Check if the component should fetch results or not. This is useful
        // when you are still waiting on additional data, like `products`.
        if (typeof shouldFetch === 'function' && !shouldFetch()) {
            return
        }

        // Do not fetch recommendations if user didn't accepted at least marketing cookies
        if (!isCustom && marketingCookieAccepted !== true) {
            return
        }

        getRecommendations(products)
    }, [
        getRecommendations,
        isAvailable,
        isCustom,
        isOnScreen,
        customerId,
        marketingCookieAccepted,
        products,
        shouldFetch
    ])

    const onRecommendationsDisplayed = useEffectEvent((recommendations) => {
        if (!isCustom) {
            sendViewReco(
                {
                    recommenderName: recommendations.recommenderName,
                    __recoUUID: recommendations.recoUUID
                },
                recommendations.recs.map((rec) => ({
                    id: rec.id
                }))
            )
        }
    })
    useEffect(() => {
        if (isOnScreen && recommendations?.recs) {
            onRecommendationsDisplayed(recommendations)
        }
    }, [isOnScreen, recommendations, onRecommendationsDisplayed])

    // Check if we have an Einstein API instance before attempting to render anything
    if (!isAvailable) {
        return null
    }

    // The component should remove itself altogether if it has no recommendations
    // and we aren't loading any.
    if (
        isOnScreen &&
        !isLoading &&
        (!recommendations || !recommendations.recs?.length || isRecommendationsHidden)
    ) {
        return null
    }

    /* Hide component if user didn't accepted at least marketing cookies */
    if (!isCustom && marketingCookieAccepted !== true) {
        return null
    }

    return (
        <Wrapper>
            <Stack ref={ref} spacing={6}>
                {(title || recommendations?.displayMessage) && (
                    <Text as="h2" {...styles.title}>
                        {title}
                    </Text>
                )}

                <Slider
                    loading={isLoading}
                    itemsPerSlide={itemsPerSlide}
                    variant="recommendations"
                    label={intl.formatMessage({
                        defaultMessage: 'Products Carousel',
                        id: 'global.carousel.products'
                    })}
                    progressLabel={intl.formatMessage({
                        defaultMessage: 'Number of products loaded',
                        id: 'recommended_products.assistive_msg.products_loaded'
                    })}
                >
                    {recommendations?.recs?.map((product, index) => (
                        <ProductTile
                            key={index}
                            variant="reco"
                            product={product}
                            dynamicImageProps={{
                                widths: ['70vw', '70vw', '40vw', '30vw']
                            }}
                            onClick={() => {
                                trackSelectItem(product, basket, trackingLabel, productItemsDetail)
                                !isCustom &&
                                    sendClickReco(
                                        {
                                            recommenderName: recommendations.recommenderName,
                                            __recoUUID: recommendations.recoUUID
                                        },
                                        product
                                    )
                            }}
                            propagateDisabled
                            {...props}
                        />
                    ))}
                </Slider>
            </Stack>
        </Wrapper>
    )
}

RecommendedProducts.propTypes = {
    /* The title to appear above the product scroller */
    title: PropTypes.any,

    /* The zone to request */
    zone: PropTypes.string,

    /* The recommender to request */
    recommender: PropTypes.string,

    /* The product IDs to use for recommendation context */
    products: PropTypes.arrayOf(PropTypes.object),

    /* Callback to determine if the component should fetch results */
    shouldFetch: PropTypes.func,

    /* Optional wrapper for the component - used when empty wrapper should not be rendered */
    wrapper: PropTypes.any,

    /* Used to change the count of items that are visible at once in a slide */
    itemsPerSlide: PropTypes.number,

    /* Tracking label */
    trackingLabel: PropTypes.string
}

export default React.memo(RecommendedProducts, (prevProps, nextProps) => {
    const propsToOmit = ['wrapper', 'title']
    return (
        isEqual(omit(prevProps, propsToOmit), omit(nextProps, propsToOmit)) &&
        prevProps?.title?.props?.id === nextProps?.title?.props?.id
    )
})
